import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { Container } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { cn } from '@reflex/ui/lib/utils';
import { SANDBOX_STATUS_CONFIG, deriveSandboxState } from '@/lib/status-colors';
import { useDevboxStatus } from '@/hooks/agents/useDevboxStatus';
import { isDevboxStatusPollEligible } from '@reflex/ui/client/hooks';
import type { AgentStatus, ResourceSize } from '@/lib/types';
import { SandboxPreview } from './SandboxPreview';
import type { SandboxMetricKey } from '@/components/sandbox/SandboxMonitorPanel';

// The monitor is a slide-out nobody has open on first paint, and it is the
// only thing on the agent page that reaches Recharts. Statically importing it
// from a status badge put the whole charting library in the entry chunk:
//
//   main.tsx -> App.tsx -> AgentsPage -> AgentStreamView -> AgentDetailPanel
//     -> SandboxStatusBadge -> SandboxMonitorPanel -> SandboxMetricChart
//     -> recharts
//
// Loading it on demand takes Recharts out of first paint entirely. The type
// import above is erased at compile time and creates no runtime edge.
const SandboxMonitorPanel = lazy(() =>
  import('@/components/sandbox/SandboxMonitorPanel').then((m) => ({
    default: m.SandboxMonitorPanel,
  })),
);

interface SandboxStatusBadgeProps {
  /**
   * Agent the devbox belongs to. When provided, the badge polls the live
   * Runloop devbox status so the indicator reflects an auto-suspended /
   * resumed box, not just the agent-status inference. Omit (e.g. in a list
   * context) to fall back to the agent-status-derived state with no polling.
   */
  agentId?: string;
  devboxId: string | null;
  agentStatus: AgentStatus;
  /**
   * Devbox resource size persisted on the agent row at launch
   * (`agent.sandboxOptions.resourceSize`). Used as a fallback for the hover
   * preview's {@link DevboxSizeBadge}: when the live devbox-status poll reports
   * a size, that live value wins; this is shown only while the poll is off or
   * hasn't resolved a size yet.
   */
  resourceSize?: ResourceSize | null;
  /**
   * Render a "Sandbox" text label beside the icon. The icon alone gives no
   * hint about what the control does, so toolbars that have room (e.g. the
   * agent header bar) opt in to the label for context. Compact clusters keep
   * the icon-only default.
   */
  withLabel?: boolean;
  /**
   * Never render an inline word next to the icon. Use in tight chrome such as
   * the agent vertical rail (`w-9`): the default error path otherwise prints
   * "Error" and clips. Color, status dot, and `aria-label` still flag the
   * failure.
   */
  iconOnly?: boolean;
  className?: string;
}

export function SandboxStatusBadge({
  agentId,
  devboxId,
  agentStatus,
  resourceSize,
  withLabel = false,
  iconOnly = false,
  className,
}: SandboxStatusBadgeProps) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [monitorOpen, setMonitorOpenState] = useState(false);
  // Latched, not derived from `monitorOpen`: unmounting the panel on close
  // would cut its slide-out animation short and re-suspend on the next open.
  // Set here in the one setter every open path goes through — the click
  // handler, the keyboard path, and the `reflex:open-sandbox-monitor` event
  // from the chat card — rather than in an effect watching `monitorOpen`,
  // which is a cascading render (`react-hooks/set-state-in-effect`).
  const [monitorEverOpened, setMonitorEverOpened] = useState(false);
  const setMonitorOpen = useCallback((next: boolean) => {
    if (next) setMonitorEverOpened(true);
    setMonitorOpenState(next);
  }, []);
  // Metric the monitor should flash + scroll to when it opens, set by the
  // performance-warning chat card via the `reflex:open-sandbox-monitor` bridge.
  const [highlightMetric, setHighlightMetric] = useState<SandboxMetricKey | undefined>(undefined);
  // Only poll when there's a devbox that could still be alive and changing.
  const pollEligible = isDevboxStatusPollEligible(agentStatus, devboxId);
  const { data: devboxStatusData } = useDevboxStatus(agentId, { enabled: pollEligible });
  // Drop the cached live status once polling stops (gone agent): TanStack
  // keeps the last value, and a stale `suspended` would otherwise win over
  // the agent-status-derived `shutdown`/`error` in deriveSandboxState.
  const liveStatus = pollEligible ? (devboxStatusData?.status ?? null) : null;
  // The same poll reports the box's live size. Prefer it over the size persisted
  // on the agent row at launch (`resourceSize`) so the badge reflects what the
  // box is actually running at; fall back to the persisted value when the poll
  // is off or hasn't resolved a size yet.
  const liveResourceSize = pollEligible ? (devboxStatusData?.resourceSize ?? null) : null;
  const effectiveResourceSize = liveResourceSize ?? resourceSize ?? null;
  // The poll also carries the box's CPU architecture (arm64 / x86_64). There is
  // no persisted fallback — the agent row never recorded one — so the hover
  // preview's arch badge only shows once the poll resolves it.
  const liveArchitecture = pollEligible ? (devboxStatusData?.architecture ?? null) : null;
  const state = deriveSandboxState(devboxId, agentStatus, liveStatus);
  const config = SANDBOX_STATUS_CONFIG[state];
  const isError = state === 'error';
  // When the caller opts in we always render the "Sandbox" label for context.
  // Otherwise the status text only surfaces inline on error so a broken
  // sandbox stands out; every other state stays icon-only.
  const labelText = iconOnly ? null : withLabel ? 'Sandbox' : isError ? config.label : null;
  const showLabel = labelText !== null;
  // The opted-in "Sandbox" label is context the icon already implies, so it is
  // dropped on phones where header space is scarce (the title + PR/servers
  // chips compete for the same row). An error label always stays visible so a
  // broken sandbox stands out regardless of viewport.
  const labelHiddenOnMobile = withLabel && !isError;
  const ariaLabel = devboxId ? `Sandbox: ${config.label}` : 'No Devbox';
  // The monitor panel is only meaningful for an attached devbox owned by a
  // known agent. When neither is present we fall back to the existing
  // hover-only behaviour so list-context badges (devboxId still unknown)
  // continue to work.
  const supportsMonitor = Boolean(agentId && devboxId);

  // Open the monitor (and flash the offending chart) when a performance
  // warning card for this agent asks us to. Mirrors the
  // `reflex:request-agent-tab` bridge used to switch agent tabs from chat.
  useEffect(() => {
    if (!supportsMonitor || !agentId) return;
    function onOpenMonitor(event: Event) {
      const detail = (event as CustomEvent<{ agentId?: string; metric?: SandboxMetricKey }>).detail;
      if (!detail?.agentId || detail.agentId !== agentId) return;
      setHoverOpen(false);
      setHighlightMetric(detail.metric);
      setMonitorOpen(true);
    }
    window.addEventListener('reflex:open-sandbox-monitor', onOpenMonitor);
    return () => window.removeEventListener('reflex:open-sandbox-monitor', onOpenMonitor);
    // `setMonitorOpen` is `useCallback([])`-stable, so declaring it re-subscribes
    // nothing; it is listed because it is now a closure over component scope
    // rather than a `useState` setter the lint rule knows to ignore.
  }, [agentId, supportsMonitor, setMonitorOpen]);

  function openMonitor(event: MouseEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    setHoverOpen(false);
    // Opening from the badge itself carries no specific metric to highlight.
    setHighlightMetric(undefined);
    setMonitorOpen(true);
  }

  function handleMonitorOpenChange(next: boolean) {
    setMonitorOpen(next);
    // Clear the highlight target once the panel closes so a later badge click
    // doesn't re-flash the previously offending chart.
    if (!next) setHighlightMetric(undefined);
  }

  function toggleHover(event: MouseEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    setHoverOpen((prev) => !prev);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (supportsMonitor) {
        setHoverOpen(false);
        setMonitorOpen(true);
      } else {
        setHoverOpen((prev) => !prev);
      }
    }
  }

  return (
    <>
      <HoverCard open={hoverOpen} onOpenChange={setHoverOpen} openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
          {/* Click opens the slide-out monitor (live metrics + threshold lines),
              hover still surfaces the at-a-glance preview for list contexts.
              When the agent has no attached devbox we fall back to the
              original click-toggles-popover behaviour. */}
          <span
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            aria-expanded={supportsMonitor ? monitorOpen : hoverOpen}
            aria-haspopup="dialog"
            data-testid="sandbox-status-badge"
            data-state={state}
            onClick={supportsMonitor ? openMonitor : toggleHover}
            onKeyDown={handleKeyDown}
            className={cn(
              'inline-flex h-7 cursor-pointer items-center justify-center rounded-md text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              showLabel ? 'gap-1.5 px-2' : 'w-7',
              labelHiddenOnMobile && 'max-sm:w-7 max-sm:gap-0 max-sm:px-0',
              isError && 'text-status-error',
              className,
            )}
          >
            <span className="relative shrink-0">
              <Container className="h-4 w-4" />
              <span
                className={cn(
                  'absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full ring-1 ring-background',
                  config.dot,
                )}
              />
            </span>
            {showLabel && (
              <span className={cn(labelHiddenOnMobile && 'max-sm:hidden')}>{labelText}</span>
            )}
          </span>
        </HoverCardTrigger>
        <HoverCardContent
          align="start"
          className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
          aria-label={ariaLabel}
        >
          <SandboxPreview
            devboxId={devboxId}
            agentStatus={agentStatus}
            devboxStatus={liveStatus}
            resourceSize={effectiveResourceSize}
            architecture={liveArchitecture}
          />
        </HoverCardContent>
      </HoverCard>
      {supportsMonitor && agentId && monitorEverOpened ? (
        // `fallback={null}`: the trigger is a badge, not a page. Rendering a
        // spinner where the slide-out will be would flash chrome over the
        // agent view for the one frame the chunk takes to arrive.
        <Suspense fallback={null}>
          <SandboxMonitorPanel
            agentId={agentId}
            devboxId={devboxId}
            agentStatus={agentStatus}
            devboxStatus={liveStatus}
            highlightMetric={highlightMetric}
            open={monitorOpen}
            onOpenChange={handleMonitorOpenChange}
          />
        </Suspense>
      ) : null}
    </>
  );
}
